import { find, get, isEmpty } from 'lodash';

// Modified by AI on 06/30/2026. Edit #1.
import React, {
    useCallback,
    useState,
    useEffect,
    useContext,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import EmailSubjectAndBodyInputComponent from '../../../../email/email-editor/EmailSubjectAndBodyInputComponentV2';
import useStyle from './MessageEditorStyle';
import Button from '../../../../material-ui-kit/CustomButtons/Button';
import ApplicationEventsContext, {
    EVENT_TYPES,
} from '../../../../../contexts/ApplicationEventsContext';
import {
    convertTextTemplateForEditor,
    convertTextTemplateForSystem,
} from '../../../../email/email-editor/template-text-plugin/TemplateTextUtils';
import SettingsContext from '../../../../../contexts/SettingsContext';
import { stripHtmlFromText } from '../../../../../widgets/rich-text-editor/TextEditor';
import CampaignContext from '../../../../../contexts/CampaignContext';

// Modified by AI on 06/30/2026. Edit #2.
// Replace RxJS Subject with callback registry pattern
const undoListeners = new Set();

export const UndoEditorChangesContext = {
    undoMessageChanges: ({ name, subject, body }) => {
        undoListeners.forEach((listener) => listener({ name, subject, body }));
    },
};

const externalSubjectChangeEvent = EVENT_TYPES.CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_SUBJECT;
const externalBodyChangeEvent = EVENT_TYPES.CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_BODY;

// Modified by AI on 05/26/2026. Edit #1.
const MessageEditor = ({
    className = '',
    type = 'email',
    messageData: externalMessageData = {},
    onTestMessageSend = () => {},
    onMessageDataSave = () => {},
    resetMessageData = () => {},
    changedMessages,
    readOnly,
    isTestMessageDisabled = false,
    isActionButtonDisabled,
    emailContextData,
    skipUrl,
    campaignSenderType,
}) => {
    const { isCampaignUpdated, setIsCampaignUpdated } = useContext(CampaignContext);
    const { dispatchEvent } = useContext(ApplicationEventsContext);
    const [messageData, setMessageData] = useState();
    const [validationState, setValidationState] = useState({
        name: true,
        subject: true,
        body: true,
    });

    const updateMessageData = () => {
        const convertedForEditorEmailSubject = convertTextTemplateForEditor(externalMessageData.subject);
        const convertedForEditorEmailBody = convertTextTemplateForEditor(externalMessageData.body);

        setMessageData({
            ...externalMessageData,
            subject: convertedForEditorEmailSubject,
            body: convertedForEditorEmailBody,
        });
        dispatchEvent({
            type: externalSubjectChangeEvent,
            data: convertedForEditorEmailSubject,
        });
        dispatchEvent({
            type: externalBodyChangeEvent,
            data: convertedForEditorEmailBody,
        });
    };

    useEffect(() => {
        updateMessageData();
    }, [
        externalMessageData.index,
        externalMessageData.channel,
        readOnly, // synced with default campaign
    ]);

    useEffect(() => {
        if (!isCampaignUpdated) {
            return;
        }
        updateMessageData();
        setIsCampaignUpdated(false);
    }, [isCampaignUpdated]);

    // Modified by AI on 06/30/2026. Edit #3.
    useEffect(() => {
        const listener = ({ name, subject, body }) => {
            setMessageData((currentState) => ({
                ...currentState,
                name,
            }));
            dispatchEvent({
                type: externalSubjectChangeEvent,
                data: subject,
            });
            dispatchEvent({
                type: externalBodyChangeEvent,
                data: body,
            });
        };
        undoListeners.add(listener);
        return () => undoListeners.delete(listener);
    }, []);

    useEffect(() => {
        setValidationState({
            name: !isEmpty(stripHtmlFromText(get(messageData, 'name'))),
            subject: !isEmpty(stripHtmlFromText(get(externalMessageData, 'subject'))),
            body: !isEmpty(stripHtmlFromText(get(externalMessageData, 'body'))),
        });
    }, [messageData, externalMessageData]);

    const isUndoEnabled = useMemo(() => !!find(changedMessages, {
        index: get(messageData, 'index'),
        channel: get(messageData, 'channel'),
    }), [changedMessages, messageData]);

    const { t } = useTranslation();
    const { systemConfiguration: { disableTestMessageChannels = ['linkedin'] } } = useContext(SettingsContext);
    const classes = useStyle({ nameFiledError: !get(validationState, 'name'), readOnly });
    const rootClasses = clsx(classes.root, className);

    const onTestMessageSendButtonClick = useCallback(() => {
        onTestMessageSend({
            subject: convertTextTemplateForSystem(externalMessageData.subject),
            body: convertTextTemplateForSystem(externalMessageData.body),
        }, skipUrl);
    }, [onTestMessageSend, externalMessageData]);

    const handleUndoChanges = useCallback(() => {
        resetMessageData({ index: messageData.index, channel: messageData.channel });
    }, [messageData, resetMessageData]);

    const handleMessageNameChange = useCallback((e) => {
        onMessageDataSave({
            index: messageData.index,
            channel: messageData.channel,
            name: e.target.value,
        });
        setMessageData((currentState) => ({
            ...currentState,
            name: e.target.value,
        }));
    }, [onMessageDataSave, messageData]);

    const handleSubjectChange = useCallback((messageSubject) => {
        const isChanged = stripHtmlFromText(messageSubject) !== stripHtmlFromText(messageData?.subject);
        if (isChanged) {
            setMessageData((currentState) => {
                onMessageDataSave({
                    index: messageData.index,
                    channel: messageData.channel,
                    name: get(externalMessageData, 'name'),
                    subject: convertTextTemplateForSystem(messageSubject),
                });
                return currentState;
            });
        }
    }, [messageData, externalMessageData, onMessageDataSave]);

    const handleBodyChange = useCallback((messageBody) => {
        const isChanged = stripHtmlFromText(messageBody) !== stripHtmlFromText(messageData.body);
        if (isChanged) {
            onMessageDataSave({
                index: messageData.index,
                channel: messageData.channel,
                name: get(externalMessageData, 'name'),
                body: convertTextTemplateForSystem(messageBody),
            });
        }
    }, [messageData, externalMessageData, onMessageDataSave]);

    return (
        <div className={rootClasses}>
            <div
                className={classes.editorHeader}
            >
                <div className={classes.messageName}>
                    <input
                        className={classes.nameInput}
                        placeholder={t('CAMPAIGN_MESSAGE_NAME_PLACEHOLDER')}
                        data-test-id='editable-label-input'
                        value={messageData?.name}
                        onChange={handleMessageNameChange}
                        disabled={readOnly}
                    />
                </div>
                <div className={classes.actionPanel}>
                    {
                        !disableTestMessageChannels.includes(get(messageData, 'channel'))
                        && <Button
                            link
                            className={classes.linkButton}
                            testId='test-message-btn'
                            color={'transparent'}
                            onClick={onTestMessageSendButtonClick}
                            disabled={isTestMessageDisabled}
                        >
                            {t('SEQUENCE_TEST_MESSAGE_MENU_ITEM')}
                        </Button>
                    }

                    <Button
                        link
                        className={classes.linkButton}
                        testId='undo-btn'
                        color={'transparent'}
                        onClick={handleUndoChanges}
                        disabled={readOnly || !isUndoEnabled}
                    >
                        {t('UNDO')}
                    </Button>
                </div>
            </div>
            <div
                className={classes.textEditorContainer}
            >
                <EmailSubjectAndBodyInputComponent
                    className={classes.emailSubjectAndBodyInput}
                    subjectClassName={classes.emailSubjectAndBodyInputSubject}
                    bodyClassName={classes.emailSubjectAndBodyInputBody}
                    disableLink={false}
                    emailContextData={emailContextData}
                    showSubject={type === 'email'}
                    emailSubject={get(messageData, 'subject')}
                    emailSubjectError={!get(validationState, 'subject')}
                    emailBody={get(messageData, 'body')}
                    emailBodyError={!get(validationState, 'body')}
                    onEmailSubjectChange={handleSubjectChange}
                    onEmailBodyChange={handleBodyChange}
                    appEventForExternalSubjectChange={externalSubjectChangeEvent}
                    appEventForExternalBodyChange={externalBodyChangeEvent}
                    type={type}
                    readOnly={readOnly}
                    isActionButtonDisabled={isActionButtonDisabled}
                    campaignSenderType={campaignSenderType}
                />
            </div>
        </div>
    );
};

MessageEditor.propTypes = {
    className: PropTypes.string,
    positionId: PropTypes.string,
    type: PropTypes.oneOf(['email', 'linkedin']),
    messageData: PropTypes.object,
    onMessageDataSave: PropTypes.func,
    onTestMessageSend: PropTypes.func,
    resetMessageData: PropTypes.func,
    changedMessages: PropTypes.array,
    readOnly: PropTypes.bool,
    isTestMessageDisabled: PropTypes.bool,
    isActionButtonDisabled: PropTypes.bool,
    emailContextData: PropTypes.object,
    skipUrl: PropTypes.bool,
    campaignSenderType: PropTypes.string,
};

export default withRouter(MessageEditor);
